import { useCallback, useState, useMemo, useEffect } from 'react';
import { useGetOrganizationsQuery } from '@/entities/organizations';

import { useUrlState, useProjectSiteParams } from '@/shared/lib/hooks';
import { useDebounce } from '@/shared/lib/hooks/use-debounce';
import { compareFilters, isDefaultFilters } from '@/shared/lib/utils';
import type { DateRangeValue, SelectOption } from '@/shared/ui';

import type { ConsignmentsUrlState } from '../model/types';
import { DEFAULT_URL_STATE } from './constants';

export const useConsignmentsFilters = () => {
  const { projectId } = useProjectSiteParams();
  const [urlState, setUrlState, resetUrlState] = useUrlState(DEFAULT_URL_STATE);
  const [organizationSearch, setOrganizationSearch] = useState('');
  const [localFilters, setLocalFilters] = useState<ConsignmentsUrlState>(() => urlState);
  const [searchQuery, setSearchQuery] = useState(() => urlState.q);

  const debouncedOrganizationSearch = useDebounce(organizationSearch, 300);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const { data: organizationsData, isLoading: isLoadingOrganizations } = useGetOrganizationsQuery(
    {
      search: debouncedOrganizationSearch || undefined,
      projectId
    },
    {
      skip: !projectId
    }
  );

  const organizationOptions: SelectOption[] = useMemo(() => {
    if (!organizationsData?.organizations) return [];

    return organizationsData.organizations.map((org) => ({
      value: org.id.toString(),
      label: org.name
    }));
  }, [organizationsData]);

  const activeFiltersCount = useMemo(() => {
    let count = 0;

    // Check date range filter (period, creationDateFrom, creationDateTo count as one filter)
    const hasDateFilter =
      (urlState.period && urlState.period !== DEFAULT_URL_STATE.period) ||
      (urlState.creationDateFrom && urlState.creationDateFrom !== '') ||
      (urlState.creationDateTo && urlState.creationDateTo !== '');

    if (hasDateFilter) {
      count++;
    }

    Object.entries(urlState).forEach(([key, value]) => {
      // Skip pagination, sorting, and date-related fields
      if (
        key === 'size' ||
        key === 'sortField' ||
        key === 'sortDirection' ||
        key === 'period' ||
        key === 'creationDateFrom' ||
        key === 'creationDateTo'
      ) {
        return;
      }

      // Count non-empty values as active filters
      if (value && value !== '' && value !== DEFAULT_URL_STATE[key as keyof ConsignmentsUrlState]) {
        count++;
      }
    });

    return count;
  }, [urlState]);

  const hasActiveFilters = activeFiltersCount > 0;

  const isFiltersDirty = useMemo(() => {
    return !compareFilters(localFilters, urlState, ['q']);
  }, [localFilters, urlState]);

  const hasDefaultFilters = useMemo(() => {
    return isDefaultFilters(urlState, DEFAULT_URL_STATE, ['q']);
  }, [urlState]);

  const handleFilterChange = useCallback((key: string, value: string) => {
    setLocalFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const setSelectedOrganization = useCallback((value?: string) => {
    const actualValue = value || '';
    setLocalFilters((prev) => ({ ...prev, orgId: actualValue }));
  }, []);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleDateRangeChange = useCallback(({ fromDate, toDate }: DateRangeValue) => {
    setLocalFilters((prev) => ({
      ...prev,
      creationDateFrom: fromDate || '',
      creationDateTo: toDate || ''
    }));
  }, []);

  const handleReset = useCallback(() => {
    setLocalFilters(DEFAULT_URL_STATE);
    resetUrlState();
  }, [resetUrlState]);

  const handleToggleAllSites = useCallback((checked: boolean) => {
    setLocalFilters((prev) => ({ ...prev, allSites: String(checked) }));
  }, []);

  const handleApplyFilters = useCallback(() => {
    const { q, ...filtersWithoutSearch } = localFilters;
    setUrlState(filtersWithoutSearch);
  }, [localFilters, setUrlState]);

  useEffect(() => {
    setLocalFilters(urlState);
  }, [urlState]);

  useEffect(() => {
    if (debouncedSearchQuery !== urlState.q) {
      setUrlState({ q: debouncedSearchQuery });
    }
  }, [debouncedSearchQuery, setUrlState, urlState.q]);

  return {
    // State
    urlState: localFilters,
    searchQuery,
    activeFiltersCount,
    hasActiveFilters,
    isFiltersDirty,
    hasDefaultFilters,

    // Organization
    organizationOptions,
    isLoadingOrganizations,
    organizationSearch,
    setOrganizationSearch,
    setSelectedOrganization,

    // Handlers
    handleFilterChange,
    handleSearchChange,
    handleDateRangeChange,
    handleReset,
    handleToggleAllSites,
    handleApplyFilters
  };
};
